import { HttpClient, OrderConfigRequest, OrderConfigResponse, SignedOrder } from '@0x/connect';
import { assetDataUtils } from '@0x/order-utils';
import { Orderbook } from '@0x/orderbook';
import { BigNumber } from '@0x/utils';
import { RateLimit } from 'async-sema';

import { RELAYER_RPS, RELAYER_URL, RELAYER_WS_URL, ZERO, ORDER_CONFIG } from '../common/constants';
import { tokenAmountInUnitsToBigNumber } from '../util/tokens';
import { Token } from '../util/types';
import { AssetProxyId } from '@0x/types';
import axios from 'axios';
import { getKnownTokens } from '../util/known_tokens';

export class Relayer {
    private readonly _client: HttpClient;
    private readonly _rateLimit: () => Promise<void>;
    private readonly _orderbook: Orderbook;

    constructor(options: { rps: number }) {
        this._orderbook = Orderbook.getOrderbookForWebsocketProvider({
            httpEndpoint: RELAYER_URL,
            websocketEndpoint: RELAYER_WS_URL,
        });
        this._client = new HttpClient(RELAYER_URL);
        this._rateLimit = RateLimit(options.rps); // requests per second
    }

    public async getAllOrdersAsync(baseTokenAssetData: string, quoteTokenAssetData: string): Promise<SignedOrder[]> {
        const [sellOrders, buyOrders] = await Promise.all([
            this._getOrdersAsync(baseTokenAssetData, quoteTokenAssetData),
            this._getOrdersAsync(quoteTokenAssetData, baseTokenAssetData),
        ]);
        return [...sellOrders, ...buyOrders];
    }
    /**
     * getOrderConfigAsync
     */
    public async _getOrderConfigAsync(orderConfig: OrderConfigRequest): Promise<OrderConfigResponse> {
        let data = {
            makerFee: ZERO,
            takerFee: ZERO,
            feeRecipientAddress: '',
            senderAddress: '',
            makerFeeAssetData: '',
            takerFeeAssetData: '',
        };
        const response = await axios.post(ORDER_CONFIG, orderConfig);
        // const MIN = new BigNumber(1);
        if (response && response.data) {
            let _data = { data, ...response.data }
            const makerFee = new BigNumber(response.data.makerFee);
            const takerFee = new BigNumber(response.data.takerFee);
            // if (makerFee.isLessThanOrEqualTo(ZERO) && !makerFee.isInteger()) {
            //     _data.makerFee = makerFee.integerValue(BigNumber.ROUND_CEIL);
            // }
            // if (takerFee.isLessThanOrEqualTo(ZERO) && !takerFee.isInteger()) {
            //     _data.takerFee = takerFee.integerValue(BigNumber.ROUND_CEIL);
            // }
            _data.makerFee = makerFee.integerValue(BigNumber.ROUND_UP);
            _data.takerFee = takerFee.integerValue(BigNumber.ROUND_UP);
            return _data;
        }
        return data;
    }

    public async getOrderConfigAsync(orderConfig: OrderConfigRequest): Promise<OrderConfigResponse> {
        await this._rateLimit();
        const { takerAssetData, makerAssetData } = orderConfig;
        const makerToken = getKnownTokens().getTokenByAssetData(makerAssetData);
        const takerToken = getKnownTokens().getTokenByAssetData(takerAssetData);
        if (makerToken && takerToken && makerToken.symbol && takerToken.symbol) {
            const _symbol = makerToken.symbol + takerToken.symbol;
            if (_symbol === 'wwxusdt' || _symbol === 'usdtwwx')
                return this._getOrderConfigAsync(orderConfig);
        }
        return this._client.getOrderConfigAsync(orderConfig);
    }

    public async getUserOrdersAsync(
        account: string,
        baseTokenAssetData: string,
        quoteTokenAssetData: string,
    ): Promise<SignedOrder[]> {
        const [sellOrders, buyOrders] = await Promise.all([
            this._getOrdersAsync(baseTokenAssetData, quoteTokenAssetData, account),
            this._getOrdersAsync(quoteTokenAssetData, baseTokenAssetData, account),
        ]);

        return [...sellOrders, ...buyOrders];
    }

    public async getCurrencyPairPriceAsync(baseToken: Token, quoteToken: Token): Promise<BigNumber | null> {
        const asks = await this._getOrdersAsync(
            assetDataUtils.encodeERC20AssetData(baseToken.address),
            assetDataUtils.encodeERC20AssetData(quoteToken.address),
        );

        if (asks.length) {
            const lowestPriceAsk = asks[0];

            const { makerAssetAmount, takerAssetAmount } = lowestPriceAsk;
            const takerAssetAmountInUnits = tokenAmountInUnitsToBigNumber(takerAssetAmount, quoteToken.decimals);
            const makerAssetAmountInUnits = tokenAmountInUnitsToBigNumber(makerAssetAmount, baseToken.decimals);
            return takerAssetAmountInUnits.div(makerAssetAmountInUnits);
        }

        return null;
    }

    public async getSellCollectibleOrdersAsync(
        collectibleAddress: string,
        wethAddress: string,
    ): Promise<SignedOrder[]> {
        await this._rateLimit();
        const result = await this._client.getOrdersAsync({
            makerAssetProxyId: AssetProxyId.ERC721,
            takerAssetProxyId: AssetProxyId.ERC20,
            makerAssetAddress: collectibleAddress,
            takerAssetAddress: wethAddress,
        });

        return result.records.map(record => record.order);
    }

    public async submitOrderAsync(order: SignedOrder): Promise<void> {
        await this._rateLimit();
        return this._client.submitOrderAsync(order);
    }

    private async _getOrdersAsync(
        makerAssetData: string,
        takerAssetData: string,
        makerAddress?: string,
    ): Promise<SignedOrder[]> {
        const apiOrders = await this._orderbook.getOrdersAsync(makerAssetData, takerAssetData);
        const orders = apiOrders.map(o => o.order);
        if (makerAddress) {
            return orders.filter(o => o.makerAddress === makerAddress);
        } else {
            return orders;
        }
    }
}

let relayer: Relayer;
export const getRelayer = (): Relayer => {
    if (!relayer) {
        relayer = new Relayer({ rps: RELAYER_RPS });
    }

    return relayer;
};
